import { useRef, useState } from 'react';
import { ArrowLeft, Bell, Check, ChevronRight, CircleCheck, CircleHelp, Clock, ClipboardCheck, Keyboard, QrCode, Search, TriangleAlert, UserCheck, X } from 'lucide-react';
import {
  useLazyLookupStudentByCodeQuery,
  useResolveCodeMutation,
  useCallToGateMutation,
  useCheckCodeMutation,
  useConfirmReleaseMutation,
  useMarkAttendanceMutation,
  getApiError,
  getApiErrorCode,
  type VerifyResult,
  type StudentLookup,
  type ResolveMatch,
  type AttendanceState,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { getDeviceId } from '@/lib/device';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { OtpInput } from '@/components/ui/OtpInput';
import { QrScanner } from '@/components/ui/QrScanner';
import { Mark } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { GateClock } from './GateClock';
import { cn } from '@/lib/cn';
import { todayWAT } from '@/lib/datetime';
import { styles } from '@/styles';

/*
  ═════════════════════════════════════════════════════════════════════════════
  The gate screen.

  An officer stands here with a queue in front of them, so this is built around
  one rule: the code is never spent until a human has looked at the child and
  said yes.

  That is why it is two API calls, not one. `/codes/check` peeks — it validates
  the code and returns the child and their authorised collectors WITHOUT
  consuming it. Only `/codes/verify`, behind the Confirm button, marks the code
  used and writes the release to the audit log. An officer who mistypes a student
  code therefore cannot burn a real child's code by accident.

  The states are explicit for the same reason: at no point should the screen be
  ambiguous about whether this child has been released.
  ═════════════════════════════════════════════════════════════════════════════
*/

type Stage =
  | { name: 'entry' }
  /* Two children in one school can hold the same six digits on the same day.
     Rare, but silently picking one would eventually release the wrong child. */
  | { name: 'pick'; matches: ResolveMatch[]; code: string }
  | { name: 'confirm'; result: VerifyResult; code: string; studentId: string }
  /* `retry` is the child the refusal was about, when there was one. It is what
     lets the register refusal offer a way forward instead of only a dead end. */
  | { name: 'error'; reason: ErrorReason; retry?: { studentId: string; code: string } }
  | { name: 'released'; studentName: string }
  | { name: 'called'; studentName: string };

/*
  Only three outcomes, because only three are distinguishable.

  The API defines CODE_003 (used) and CODE_004 (revoked), but codes.service.ts
  never throws them: both the Redis and the bcrypt path look the code up with
  `{ isRevoked: false, isUsed: false }`, so a used or cancelled code simply is
  not found and comes back as CODE_001 — the same as a wrong code. Verified
  against the running API: wrong, revoked and used all return
  `{"code":"CODE_001","message":"Invalid pickup code."}`.

  So this screen does not claim to tell them apart. The "not recognised" copy
  names all three possibilities instead, which is what the officer needs in order
  to know what to ask the parent. Pretending to distinguish them would be worse
  than not distinguishing them: an officer told "wrong code" for a code that was
  actually already used would keep retyping it instead of raising the alarm.

  STUDENT_ABSENT and ATTENDANCE_UNKNOWN are genuinely distinct and are handled,
  because releasing a child the school has marked absent — or one nobody has
  confirmed is in school at all — is exactly the case someone must be told about
  rather than left to retype a code that will never work.

  And the two are not the same refusal. Absent means the school looked and the
  child is not here: nothing at the gate can fix that. Unmarked means the school
  has not looked, which somebody can fix in a minute. Collapsing them into one
  message would send a parent to the office when a teacher could have answered
  it from the classroom.
*/
type ErrorReason = 'invalid' | 'expired' | 'absent' | 'unmarked' | 'rateLimited';

const REASONS: Record<ErrorReason, { title: string; body: string }> = {
  invalid: {
    title: 'Code not accepted',
    body: "This is not a working code for this child right now. It may be mistyped, it may already have been used today, or the parent may have cancelled it. Ask them to read out their current code — and check you have the right child.",
  },
  expired: {
    title: 'Code has expired',
    body: "Codes are valid until 23:59. This one is past that, so it can no longer release this child. Call the school office.",
  },
  /*
    A lockout used to arrive here as "invalid", so an officer retyped a correct
    code and was told it was wrong — the real cause never surfaced. The API now
    answers 429/CODE_005 and this says what to do about it.
  */
  rateLimited: {
    title: 'This device is locked for a few minutes',
    body: 'Too many wrong codes were entered here. The lock is on this device only — another officer can carry on while it clears. If the parent is sure of their code, send them to the school office rather than waiting.',
  },
  absent: {
    title: 'Marked absent today',
    body: 'The school recorded this child as absent, so no code can release them. Do not release the child — send whoever is collecting to the school office.',
  },
  unmarked: {
    title: 'No register for this child today',
    body: 'Nobody has recorded whether this child is in school, so they cannot be released yet. The code is fine. Ask the class teacher to take the register — the gate will accept the same code the moment they do.',
  },
};

function reasonFor(error: unknown): ErrorReason {
  const code = getApiErrorCode(error);
  if (code === 'STUDENT_ABSENT') return 'absent';
  if (code === 'ATTENDANCE_UNKNOWN') return 'unmarked';
  if (code === 'CODE_005') return 'rateLimited';
  if (code === 'CODE_002') return 'expired';
  return 'invalid';
}

/*
  How the register's answer reads on this screen.

  The same three words everywhere — the confirm panel, the picker, the
  refusal — so an officer never has to work out whether two phrasings mean the
  same thing while a parent waits.
*/
const ATTENDANCE_LABEL: Record<AttendanceState, string> = {
  present: 'Present',
  absent: 'Absent',
  unmarked: 'Not marked',
};

const ATTENDANCE_TONE: Record<AttendanceState, 'success' | 'danger' | 'warning'> = {
  present: 'success',
  absent: 'danger',
  unmarked: 'warning',
};

/*
  The clock sits above every stage, so it is still there while an officer is
  looking at a child's photo or reading a refusal — the two moments they are
  most likely to be asked the time.
*/
export function GateVerifyPage() {
  return (
    <div className="flex flex-col gap-3">
      <GateClock />
      <GateVerify />
    </div>
  );
}

function GateVerify() {
  const { user } = useAuthStore();
  const deviceId = useRef(getDeviceId()).current;

  const [stage, setStage] = useState<Stage>({ name: 'entry' });
  /*
    Typing is the default, not scanning.

    A QR only exists if the parent opened the app; the six digits arrive by SMS
    and therefore work for every family, including the ones without a
    smartphone — which is most of the reason SafePass is SMS-first. Opening on
    the camera would put the narrower path in front of the wider one, and it
    would ask for a camera permission from schools that never scan anything.
  */
  const [entryMode, setEntryMode] = useState<'type' | 'scan'>('type');
  const [studentCode, setStudentCode] = useState('');
  const [student, setStudent] = useState<StudentLookup | null>(null);
  const [lookupError, setLookupError] = useState('');
  const [pickupCode, setPickupCode] = useState('');

  const [lookup, lookupState] = useLazyLookupStudentByCodeQuery();
  const [resolveCode, resolveState] = useResolveCodeMutation();
  const [checkCode, checkState] = useCheckCodeMutation();
  const [confirmRelease, releaseState] = useConfirmReleaseMutation();
  const [callToGate, callState] = useCallToGateMutation();
  const [markAttendance, markState] = useMarkAttendanceMutation();

  const institutionId = user?.institutionId ?? '';

  /*
    Who may resolve a missing register from here.

    Only the roles the attendance API already trusts — a head teacher covering
    the gate, not a gate officer. A gate officer gets told who to ask instead,
    which is the point: the person who decides a child is in school should be
    the person who can see the child, and at the gate that is not true.
  */
  const canTakeRegister = user?.role === 'institution_admin' || user?.role === 'super_admin';

  const reset = () => {
    setStage({ name: 'entry' });
    setStudentCode('');
    setStudent(null);
    setLookupError('');
    setPickupCode('');
  };

  const doLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = studentCode.trim().toUpperCase();
    if (!code) return;
    setLookupError('');
    setStudent(null);
    try {
      const found = await lookup({ institutionId, studentCode: code }).unwrap();
      setStudent(found);
      setStudentCode(code);
    } catch {
      setLookupError(`No child on the register with code ${code}.`);
    }
  };

  /*
    Six digits, no student code.

    The officer used to have to ask "what is your child's student code?" before
    anything else — eleven digits typed per family, and a question parents
    routinely cannot answer, stalling the queue while somebody looks it up. The
    code the parent was texted this morning is now enough to find the child.
  */
  const doResolve = async (code: string) => {
    if (code.length !== 6) return;
    try {
      const { matches } = await resolveCode({ code, deviceId }).unwrap();
      if (matches.length === 1) {
        await doCheckFor(matches[0]!.studentId, code);
      } else {
        setStage({ name: 'pick', matches, code });
      }
    } catch (err) {
      setStage({ name: 'error', reason: reasonFor(err) });
      setPickupCode('');
    }
  };

  /* The peek. Does not consume the code — see the note at the top. */
  const doCheckFor = async (studentId: string, code: string) => {
    if (code.length !== 6) return;
    try {
      const result = await checkCode({
        code,
        studentId,
        deviceId,
        gateOfficerId: user?.id ?? '',
        institutionId,
      }).unwrap();
      setStage({ name: 'confirm', result, code, studentId });
    } catch (err) {
      setStage({ name: 'error', reason: reasonFor(err), retry: { studentId, code } });
      setPickupCode('');
    }
  };

  /*
    Take the register for this one child, from the gate, then carry on.

    Only offered to the roles that may mark attendance anyway — without it they
    would open the register in another tab and do exactly this, with the same
    authority and less of a record. It writes a 'present' decision in their
    name, so the override is a recorded act rather than a bypass, and it then
    re-runs the ordinary check: the code still has to be right.
  */
  const doMarkPresentAndContinue = async () => {
    if (stage.name !== 'error' || !stage.retry) return;
    const { studentId, code } = stage.retry;
    try {
      await markAttendance({
        institutionId,
        date: todayWAT(),
        studentIds: [studentId],
        status: 'present',
      }).unwrap();
      await doCheckFor(studentId, code);
    } catch {
      /* The refusal stays on screen; the button reports its own failure. */
    }
  };

  /* With a child already chosen by the fallback search, go straight to the
     check; otherwise let six digits find them. */
  const submitCode = (code: string) =>
    student ? doCheckFor(student.studentId, code) : doResolve(code);

  /*
    A scanned QR, which carries the same two things the officer would otherwise
    type: `safepass://verify?code=XXXXXX&studentId=YYYYYY`.

    It is a shortcut to the SAME peek, not a shortcut past it. The officer still
    sees the child's photo, the authorised collectors and the register on the
    next screen, and still presses Confirm release. Scanning identifies; a human
    still decides. Anything unparseable is ignored rather than guessed at — a
    QR from some other app must not become a pickup attempt.
  */
  const doScan = (text: string) => {
    if (checkState.isLoading || resolveState.isLoading) return;
    let code = '';
    let studentId = '';
    try {
      const url = new URL(text.trim());
      code = url.searchParams.get('code') ?? '';
      studentId = url.searchParams.get('studentId') ?? '';
    } catch {
      /* Not a URL. A QR holding just the six digits is still usable. */
      const bare = text.trim();
      if (/^\d{6}$/.test(bare)) code = bare;
    }
    if (!/^\d{6}$/.test(code)) return;

    setPickupCode(code);
    if (studentId) void doCheckFor(studentId, code);
    else void doResolve(code);
  };

  /*
    Summon the child without releasing them.

    This is the half of dismissal that can happen before the parent reaches the
    window — an officer working the queue of cars taps it and the classroom sees
    the child on the board immediately, so the walk happens while the parent is
    still queueing instead of after. The code is NOT spent: the parent still
    presents it here when they collect.
  */
  const doCall = async () => {
    if (stage.name !== 'confirm') return;
    try {
      await callToGate({ institutionId, studentId: stage.studentId }).unwrap();
      setStage({ name: 'called', studentName: stage.result.studentName });
    } catch (err) {
      setStage({ name: 'error', reason: reasonFor(err) });
    }
  };

  /* The commit. This is the call that marks the code used. */
  const doRelease = async () => {
    if (stage.name !== 'confirm') return;
    try {
      await confirmRelease({
        code: stage.code,
        studentId: stage.studentId,
        deviceId,
        gateOfficerId: user?.id ?? '',
        institutionId,
      }).unwrap();
      setStage({ name: 'released', studentName: stage.result.studentName });
    } catch (err) {
      setStage({ name: 'error', reason: reasonFor(err) });
    }
  };

  /* ── Released ─────────────────────────────────────────────────────────── */
  if (stage.name === 'released') {
    return (
      <div className={cn(styles.panel, 'overflow-clip')}>
        <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-lg border border-success/30 bg-success/[0.09]">
            <CircleCheck size={28} className="text-[hsl(var(--record-ink-success))]" />
          </span>
          <div>
            <p className="text-[18px] font-bold text-foreground">Released</p>
            <p className={cn(styles.meta, 'mt-1')}>
              {stage.studentName} has been signed out. The parent has been notified by SMS.
            </p>
          </div>
          <Button variant="primary" size="lg" className="mt-1 w-full max-w-[280px]" onClick={reset}>
            Next child
          </Button>
        </div>
      </div>
    );
  }

  /* ── Called — on the board, not yet collected ─────────────────────────── */
  if (stage.name === 'called') {
    return (
      <div className={cn(styles.panel, 'overflow-clip')}>
        <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-lg border border-info/30 bg-info/[0.09]">
            <Bell size={28} className="text-[hsl(var(--record-ink-info))]" />
          </span>
          <div>
            <p className="text-[18px] font-bold text-foreground">Called to the gate</p>
            <p className={cn(styles.meta, 'mx-auto mt-1 max-w-[340px] leading-relaxed')}>
              {stage.studentName} is on the dismissal board and their class can send them down.
              They have <strong>not</strong> been released — take the code again when they are
              handed over.
            </p>
          </div>
          <Button variant="primary" size="lg" className="mt-1 w-full max-w-[280px]" onClick={reset}>
            Next child
          </Button>
        </div>
      </div>
    );
  }

  /* ── Failed ───────────────────────────────────────────────────────────── */
  if (stage.name === 'error') {
    const { title, body } = REASONS[stage.reason];
    /* Whether something above it is already the primary action. */
    const hasOtherAction =
      stage.reason === 'invalid' || (stage.reason === 'unmarked' && canTakeRegister && Boolean(stage.retry));
    return (
      <div className={cn(styles.panel, 'overflow-clip')}>
        <div className="flex flex-col items-center gap-3 px-5 py-9 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-lg border border-destructive/30 bg-destructive/[0.08]">
            <X size={28} className="text-[hsl(var(--record-ink-danger))]" />
          </span>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--record-ink-danger))]">
              Do not release
            </p>
            <p className="mt-1.5 text-[17px] font-bold text-foreground">{title}</p>
            <p className={cn(styles.meta, 'mx-auto mt-1.5 max-w-[380px] leading-relaxed')}>{body}</p>
          </div>

          {stage.reason === 'invalid' && (
            <div className="mt-1 flex items-start gap-2 rounded-md border border-warning/30 bg-warning/[0.08] px-3 py-2 text-left">
              <TriangleAlert size={13} className="mt-[2px] flex-shrink-0 text-[hsl(var(--record-ink-warning))]" />
              <p className="text-[11px] leading-relaxed text-[hsl(var(--record-ink-warning))]">
                After 3 failed attempts for the same child, the school office is alerted
                automatically. Every attempt is recorded.
              </p>
            </div>
          )}

          {stage.reason === 'unmarked' && !canTakeRegister && (
            <div className="mt-1 flex items-start gap-2 rounded-md border border-info/30 bg-info/[0.07] px-3 py-2 text-left">
              <TriangleAlert size={13} className="mt-[2px] flex-shrink-0 text-[hsl(var(--record-ink-info))]" />
              <p className="text-[11px] leading-relaxed text-[hsl(var(--record-ink-info))]">
                You cannot take the register from the gate. Ask the class teacher or the office to
                mark the class, then try the same code again — it has not been used.
              </p>
            </div>
          )}

          {markState.error && (
            <div
              role="alert"
              className="mt-1 flex items-start gap-2 rounded-md border border-destructive/25 bg-destructive/[0.06] px-3 py-2 text-left text-[11px] font-medium text-[hsl(var(--record-ink-danger))]"
            >
              <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
              {getApiError(markState.error)}
            </div>
          )}

          <div className="mt-1 flex w-full max-w-[320px] flex-col gap-2">
            {/*
              The register refusal is the one refusal with a way forward, and
              only for the roles that may mark attendance anyway. It records a
              'present' decision in their name and then re-runs the ordinary
              check — the code still has to be right.
            */}
            {stage.reason === 'unmarked' && canTakeRegister && stage.retry && (
              <Button
                variant="primary"
                size="lg"
                loading={markState.isLoading || checkState.isLoading}
                onClick={() => void doMarkPresentAndContinue()}
              >
                This child is here — mark present and continue
              </Button>
            )}
            {stage.reason === 'invalid' && (
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  setPickupCode('');
                  setStage({ name: 'entry' });
                }}
              >
                Try the code again
              </Button>
            )}
            <Button
              variant={hasOtherAction ? 'secondary' : 'primary'}
              size={hasOtherAction ? 'md' : 'lg'}
              onClick={reset}
            >
              Start over with a different child
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Confirm — identity verified, not yet released ────────────────────── */
  if (stage.name === 'confirm') {
    const { result } = stage;
    const primary = result.collectors.find((c) => c.isPrimary) ?? result.collectors[0];

    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2.5 rounded-lg border border-success/30 bg-success/[0.07] px-3.5 py-3">
          <Check size={17} className="flex-shrink-0 text-[hsl(var(--record-ink-success))]" />
          <div>
            <p className="text-[13px] font-bold text-[hsl(var(--record-ink-success))]">Code is valid</p>
            <p className="text-[11px] text-[hsl(var(--record-ink-success))]/80">
              Check the child and the adult, then confirm.
            </p>
          </div>
        </div>

        <div className={cn(styles.panel, 'overflow-clip')}>
          <div className="flex items-center gap-3.5 px-4 py-4">
            {/* The whole point of this screen is comparing a face against a photo, so
                this one in particular has to be openable. */}
            <Avatar
              src={result.studentPhotoUrl}
              name={result.studentName}
              size={80}
              zoomable
              caption={result.studentClass}
            />
            <div className="min-w-0">
              <p className={styles.label}>Child</p>
              <p className="mt-0.5 text-[19px] font-bold leading-tight text-foreground">
                {result.studentName}
              </p>
              <p className="mt-0.5 text-[12px] text-muted-foreground">{result.studentClass}</p>
            </div>
          </div>

          {/*
            Stated, not assumed. The gate has already refused absent and
            unmarked children by this point, so this line is never the thing
            that stops a release — it is the thing that lets an officer answer
            "is he even in school today?" without leaving the screen, and it is
            the sentence they can repeat back to a parent.
          */}
          <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2.5">
            <div className="flex items-center gap-2">
              {result.attendanceStatus === 'present' ? (
                <ClipboardCheck size={15} className="flex-shrink-0 text-[hsl(var(--record-ink-success))]" />
              ) : (
                <CircleHelp size={15} className="flex-shrink-0 text-[hsl(var(--record-ink-warning))]" />
              )}
              <div>
                <p className={styles.label}>Register today</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {result.attendanceMarkedAt
                    ? `Marked at ${new Date(result.attendanceMarkedAt).toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`
                    : 'Nobody has taken the register for this child'}
                </p>
              </div>
            </div>
            <Mark tone={ATTENDANCE_TONE[result.attendanceStatus]}>
              {ATTENDANCE_LABEL[result.attendanceStatus]}
            </Mark>
          </div>

          <div className="border-t border-border px-4 py-3">
            <p className={styles.label}>Authorised to collect</p>
            <div className="mt-2 flex flex-col gap-1.5">
              {result.collectors.length === 0 ? (
                <p className="text-[11.5px] text-[hsl(var(--record-ink-warning))]">
                  No collectors are on file for this child. Confirm with the school office before
                  releasing.
                </p>
              ) : (
                result.collectors.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center gap-2.5 rounded-md border border-border bg-muted/50 px-2.5 py-2"
                  >
                    <Avatar src={c.photoUrl} name={c.fullName} size={36} zoomable caption={c.relationship} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[12.5px] font-bold text-foreground">{c.fullName}</p>
                      <p className="truncate text-[11px] capitalize text-muted-foreground">
                        {c.relationship}
                      </p>
                    </div>
                    {c.isPrimary && <Mark tone="info">Primary</Mark>}
                  </div>
                ))
              )}
            </div>
            {primary && (
              <p className="mt-2.5 text-[11px] leading-relaxed text-muted-foreground">
                Only release to someone on this list. If the person at the gate is not here, do not
                release the child — ask them to call the parent.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-border px-4 py-3">
            {releaseState.error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-destructive/25 bg-destructive/[0.06] px-2.5 py-2 text-[11px] font-medium text-[hsl(var(--record-ink-danger))]"
              >
                <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
                {getApiError(releaseState.error)}
              </div>
            )}
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              icon={<UserCheck size={15} />}
              loading={releaseState.isLoading}
              onClick={() => void doRelease()}
            >
              Confirm release
            </Button>
            {/*
              Two different moments, two different buttons. The child standing
              here gets released; the child still in class gets called, and the
              parent presents the code again when they arrive.
            */}
            <Button
              variant="secondary"
              size="md"
              className="w-full"
              icon={<Bell size={14} />}
              loading={callState.isLoading}
              disabled={releaseState.isLoading}
              onClick={() => void doCall()}
            >
              Not here yet — call them to the gate
            </Button>
            <Button
              variant="ghost"
              size="md"
              className="w-full"
              onClick={reset}
              disabled={releaseState.isLoading || callState.isLoading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Pick — two children share these six digits ───────────────────────── */
  if (stage.name === 'pick') {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-2.5 rounded-lg border border-warning/30 bg-warning/[0.08] px-3.5 py-3">
          <TriangleAlert size={16} className="mt-[1px] flex-shrink-0 text-[hsl(var(--record-ink-warning))]" />
          <div>
            <p className="text-[13px] font-bold text-[hsl(var(--record-ink-warning))]">
              Two children have this code today
            </p>
            <p className="text-[11px] leading-relaxed text-[hsl(var(--record-ink-warning))]/85">
              Codes are random, so this happens occasionally. Check the photo against the child in
              front of you — not the name.
            </p>
          </div>
        </div>

        <div className={cn(styles.panel, 'overflow-clip')}>
          {stage.matches.map((m) => (
            <button
              key={m.studentId}
              type="button"
              onClick={() => void doCheckFor(m.studentId, stage.code)}
              disabled={checkState.isLoading}
              className="flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
            >
              <Avatar src={m.photoUrl} name={m.fullName} size={52} zoomable caption={m.studentClass} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-bold text-foreground">{m.fullName}</p>
                <p className="truncate text-[11.5px] text-muted-foreground">{m.studentClass}</p>
              </div>
              {/* Which of the two the register says is even in school, before
                  the officer picks one and gets refused. */}
              <Mark tone={ATTENDANCE_TONE[m.attendanceStatus]}>
                {ATTENDANCE_LABEL[m.attendanceStatus]}
              </Mark>
              <ChevronRight size={16} className="flex-shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>

        <Button variant="secondary" size="md" onClick={reset}>
          Start over
        </Button>
      </div>
    );
  }

  /* ── Entry ────────────────────────────────────────────────────────────── */
  return (
    <div className="flex flex-col gap-3">
      <div>
        <h1 className={styles.pageTitle}>Verify a pickup</h1>
        <p className={cn(styles.meta, 'mt-0.5')}>
          Ask for the six-digit code, or scan the QR in the parent's app.
        </p>
      </div>

      <div className={cn(styles.panel, 'overflow-clip')}>
        <div className="px-4 pb-4 pt-4">
          {/*
            When the fallback search has already picked a child, say so — the
            same six boxes mean "check this child's code" rather than "find a
            child", and the officer must be able to see which.
          */}
          {student && (
            <div className="mb-3 flex items-center gap-2.5 rounded-md border border-info/25 bg-info/[0.07] px-3 py-2.5">
              <Check size={15} className="flex-shrink-0 text-[hsl(var(--record-ink-info))]" />
              <div className="min-w-0">
                <p className="truncate text-[13px] font-bold text-foreground">{student.fullName}</p>
                <p className="truncate text-[11px] text-muted-foreground">{student.studentClass}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setStudent(null);
                  setStudentCode('');
                  setPickupCode('');
                }}
                className="ml-auto flex flex-shrink-0 items-center gap-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft size={11} />
                Clear
              </button>
            </div>
          )}

          <div className="mb-2 flex items-center justify-between gap-2">
            <p className={styles.label}>
              {student ? "This child's pickup code" : 'Pickup code'}
            </p>

            {/*
              A two-way switch rather than a scanner that is always running.
              A camera preview left live on a gate screen is a camera pointed at
              a queue of children all afternoon, and nobody asked for that.
            */}
            <div className="flex items-center gap-0.5 rounded-md border border-border bg-muted/50 p-0.5">
              {(['type', 'scan'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setEntryMode(mode)}
                  aria-pressed={entryMode === mode}
                  className={cn(
                    'flex items-center gap-1 rounded px-2 py-1 text-[10.5px] font-bold uppercase tracking-[0.05em] transition-colors',
                    entryMode === mode
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {mode === 'type' ? <Keyboard size={12} /> : <QrCode size={12} />}
                  {mode === 'type' ? 'Type' : 'Scan'}
                </button>
              ))}
            </div>
          </div>

          {entryMode === 'scan' ? (
            <>
              <QrScanner
                onScan={doScan}
                /* Stop decoding while a lookup is in flight, so a code held in
                   front of the lens cannot fire a second request. */
                paused={checkState.isLoading || resolveState.isLoading}
                className="aspect-[4/3] w-full"
              />
              {(checkState.isLoading || resolveState.isLoading) && (
                <p className="mt-2 text-center text-[11px] font-semibold text-muted-foreground">
                  Looking up that code…
                </p>
              )}
              <Button
                variant="secondary"
                size="md"
                className="mt-3 w-full"
                icon={<Keyboard size={14} />}
                onClick={() => setEntryMode('type')}
              >
                No QR — type the six digits
              </Button>
            </>
          ) : (
          <>
          <OtpInput
            value={pickupCode}
            onChange={setPickupCode}
            onComplete={(code) => void submitCode(code)}
            disabled={checkState.isLoading || resolveState.isLoading}
            invalid={Boolean(checkState.error || resolveState.error)}
          />

          <Button
            variant="primary"
            size="lg"
            className="mt-3 w-full"
            loading={checkState.isLoading || resolveState.isLoading}
            disabled={pickupCode.length !== 6 || !institutionId}
            onClick={() => void submitCode(pickupCode)}
          >
            Find child
          </Button>

          </>
          )}

          <p className="mt-2 text-[10.5px] leading-relaxed text-muted-foreground">
            This does not release the child and does not use up the code — you see their photo and
            confirm on the next screen.
          </p>
        </div>

        {/*
          The old two-step flow, kept as a fallback rather than deleted.

          Six digits finds the child from the code the parent was texted this
          morning. It cannot help when the parent has lost the text — so the
          student-code search is still here, one disclosure away, for the handful
          of families that need it each day.
        */}
        <details className="border-t border-border">
          <summary className="cursor-pointer px-4 py-2.5 text-[11.5px] font-semibold text-muted-foreground transition-colors hover:text-foreground">
            Parent does not have the code?
          </summary>

          <form onSubmit={doLookup} className="px-4 pb-3.5" noValidate>
            <Field
              label="Student code"
              htmlFor="student-code"
              error={lookupError || undefined}
              hint="On the child's record — look it up in the office. You will still need a working pickup code to release them."
            >
              <div className="flex gap-2">
                <Input
                  id="student-code"
                  placeholder="12345"
                  className="font-mono uppercase tracking-[0.12em]"
                  value={studentCode}
                  invalid={Boolean(lookupError)}
                  onChange={(e) => {
                    setStudentCode(e.target.value.toUpperCase());
                    setStudent(null);
                    setLookupError('');
                  }}
                />
                <Button
                  type="submit"
                  variant="secondary"
                  size="md"
                  icon={<Search size={13} />}
                  loading={lookupState.isLoading}
                  disabled={!studentCode.trim() || !institutionId}
                  className="flex-shrink-0"
                >
                  Find
                </Button>
              </div>
            </Field>
          </form>
        </details>
      </div>

      <p className="flex items-start gap-1.5 px-1 text-[10.5px] leading-relaxed text-muted-foreground">
        <Clock size={12} className="mt-[1px] flex-shrink-0" />
        Five wrong codes locks this device for ten minutes. Another officer's device keeps working.
      </p>
    </div>
  );
}
